import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useCallback } from 'react';
import { IconArrowLeftSolid, IconArrowRightSolid } from '@circlefin/euclid-icons/common';
import classNames from 'classnames';
import { Button } from '../../Button/Button.es.js';

const AdvancedTablePages = ({ getPageCount, getPageOptions, getState, setPageIndex, nextPage, previousPage, getCanNextPage, getCanPreviousPage, nextLabel, prevLabel, loading })=>{
    const handleClick = useCallback((index)=>()=>setPageIndex(index), [
        setPageIndex
    ]);
    const { pagination: { pageIndex } } = getState();
    const pageCount = getPageCount();
    const pageOptions = getPageOptions();
    const canNextPage = getCanNextPage();
    const canPreviousPage = getCanPreviousPage();
    if (/**
     * No need pagination for 1 or fewer pages
     * -1 is used by react-table to hide the page numbers.
     */ pageCount !== -1 && pageCount <= 1 || /**
         * No need to show the pagination when we don't show the page numbers,
         * and we can't go back or forth.
         */ !loading && pageCount === -1 && !canPreviousPage && !canNextPage || loading && pageIndex === 0) return null;
    /**
     * Calculate remaining number of pages.
     */ const remaining = pageCount - pageIndex;
    /**
     * Render page button(s) from array of page numbers.
     */ const renderSection = (section)=>section.map((index)=>jsx(Button, {
                className: classNames('page', {
                    active: pageIndex === index
                }),
                "data-testid": "control-number",
                onClick: handleClick(index),
                variant: "text",
                children: index + 1
            }, `page-${index}`));
    /**
     * Ellipsis button to display between pages.
     */ const Ellipsis = ()=>jsx(Button, {
            className: "page",
            variant: "text",
            children: "..."
        });
    return jsxs("div", {
        className: "cb-advanced-table-pagination",
        "data-testid": "pagination",
        children: [
            jsxs(Button, {
                className: classNames('pagination-button prev-button', {
                    hide: !canPreviousPage
                }),
                "data-testid": "prev-button",
                disabled: !canPreviousPage,
                onClick: previousPage,
                variant: "text",
                children: [
                    jsx(IconArrowLeftSolid, {
                        size: 16
                    }),
                    " ",
                    prevLabel
                ]
            }),
            pageCount !== -1 && jsx("div", {
                className: "pages",
                children: pageCount <= 6 ? renderSection(pageOptions.slice(0, pageCount)) : jsxs(Fragment, {
                    children: [
                        // Always show the first 5 pages, if you are on page 3 or earlier, afterwards we will make it dynamic based on page index
                        pageIndex < 2 && renderSection(pageOptions.slice(0, Math.min(5, pageCount))),
                        // Always show page 1 if you are on a page 4 or greater (this will be grouped with current pages if on page 4 to show first 5 pages)
                        pageIndex > 2 && renderSection([
                            0
                        ]),
                        // Always show ellipsis if you are on page 5 or greater
                        pageIndex > 3 && jsx(Ellipsis, {}),
                        // Safeguard against rendering negative pages or above pageCount
                        pageIndex >= 2 && remaining > 3 && renderSection(pageOptions.slice(Math.max(0, pageIndex - 2), Math.min(pageIndex + 3, pageCount))),
                        // Always show ellipsis if remaining pages is > 4
                        remaining > 4 && jsx(Ellipsis, {}),
                        // Always show last page if remaining pages is > 3 (this will be grouped with current pages rendered above if remaining === 3 to show last 4 pages)
                        remaining > 3 && renderSection(pageOptions.slice(-1)),
                        // Always show the last 5 pages, if we have only 3 or less remaining pages
                        pageIndex >= 2 && remaining <= 3 && renderSection(pageOptions.slice(pageCount - 5, pageCount))
                    ]
                })
            }),
            jsxs(Button, {
                className: classNames('pagination-button next-button', {
                    hide: !canNextPage
                }),
                "data-testid": "next-button",
                disabled: !canNextPage,
                onClick: nextPage,
                variant: "text",
                children: [
                    nextLabel,
                    " ",
                    jsx(IconArrowRightSolid, {
                        size: 16
                    })
                ]
            })
        ]
    });
};

export { AdvancedTablePages };
//# sourceMappingURL=AdvancedTablePages.es.js.map
